import { useRef, type MouseEvent as ReactMouseEvent } from "react";
import {
  motion,
  useMotionTemplate,
  useMotionValue,
  useSpring,
} from "motion/react";
import { SectionHeading } from "../ui/SectionHeading";
import { Reveal } from "../ui/Reveal";
import { projects, type Project } from "../../data/content";

function TiltCard({ project }: { project: Project }) {
  const ref = useRef<HTMLDivElement>(null);

  const rx = useMotionValue(0);
  const ry = useMotionValue(0);
  const srx = useSpring(rx, { stiffness: 140, damping: 18 });
  const sry = useSpring(ry, { stiffness: 140, damping: 18 });

  const px = useMotionValue(50);
  const py = useMotionValue(50);
  const spotlight = useMotionTemplate`radial-gradient(420px circle at ${px}% ${py}%, rgba(255,255,255,0.14), transparent 65%)`;

  const onMove = (e: ReactMouseEvent) => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    px.set(x * 100);
    py.set(y * 100);
    ry.set((x - 0.5) * 10);
    rx.set((0.5 - y) * 10);
  };

  const onLeave = () => {
    rx.set(0);
    ry.set(0);
    px.set(50);
    py.set(50);
  };

  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      style={{ rotateX: srx, rotateY: sry, transformStyle: "preserve-3d", perspective: 1200 }}
      className="group relative h-full"
    >
      <div className="relative flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-white/10 bg-surface/70 backdrop-blur transition-colors duration-500 hover:border-white/25">
        {/* Cover */}
        <div
          className="relative flex h-52 items-center justify-center overflow-hidden"
          style={{ background: `radial-gradient(circle at 30% 20%, ${project.accent}55, transparent 60%), linear-gradient(135deg, #0c0f1a, #080a12)` }}
        >
          <span
            className="absolute -bottom-6 -right-2 select-none font-display text-[9rem] font-extrabold leading-none opacity-15"
            style={{ color: project.accent }}
          >
            {project.index}
          </span>
          <div
            className="relative grid h-20 w-20 place-items-center rounded-2xl border border-white/15 font-display text-3xl font-bold text-white/90 shadow-2xl"
            style={{ background: `linear-gradient(135deg, ${project.accent}66, transparent)` }}
          >
            {project.title.charAt(0)}
          </div>
          <span
            className="absolute left-6 top-5 rounded-full border border-white/15 bg-black/30 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-white/70 backdrop-blur"
          >
            {project.category}
          </span>
        </div>

        {/* Body */}
        <div className="relative flex flex-1 flex-col p-7">
          <p className="font-mono text-xs tracking-[0.3em] text-muted">{project.index}</p>
          <h3 className="mt-2 font-display text-2xl font-bold text-white transition-colors duration-300 group-hover:text-transparent group-hover:[background:linear-gradient(110deg,#fff,#c4b5fd)] group-hover:[background-clip:text] group-hover:[-webkit-background-clip:text]">
            {project.title}
          </h3>
          <p className="mt-3 flex-1 text-sm leading-relaxed text-white/55">
            {project.description}
          </p>

          <div className="mt-6 flex flex-wrap gap-2">
            {project.tags.map((t) => (
              <span
                key={t}
                className="rounded-full border border-white/10 bg-white/5 px-3 py-1 font-mono text-[11px] text-white/70"
              >
                {t}
              </span>
            ))}
          </div>

          <a
            href={project.link}
            className="mt-6 inline-flex items-center gap-2 text-sm font-medium text-white/80 transition-colors hover:text-white"
            data-cursor
          >
            View Project
            <span className="transition-transform duration-300 group-hover:translate-x-1.5">→</span>
          </a>
        </div>

        {/* Spotlight on hover */}
        <motion.div
          className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
          style={{ background: spotlight }}
        />
      </div>
    </motion.div>
  );
}

export function Projects() {
  return (
    <section id="work" className="section">
      <div className="mx-auto max-w-6xl px-6 md:px-10">
        <div className="flex flex-col justify-between gap-6 md:flex-row md:items-end">
          <SectionHeading eyebrow="03 · Work" title="Selected" accent="projects." />
          <Reveal delay={0.2}>
            <a
              href="https://www.behance.net/snehasudhir"
              target="_blank"
              rel="noreferrer"
              className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-muted transition-colors hover:text-white"
              data-cursor
            >
              View all work
              <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>
            </a>
          </Reveal>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {projects.map((project, i) => (
            <Reveal key={project.title} delay={(i % 2) * 0.12}>
              <TiltCard project={project} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
